import { DEFAULT_INFERENCE_SETTINGS, IInferenceModel, NotificationType, useApp, useTranslation } from "@next-platform/client-common-core";
import { getAvailableMemory } from "@next-platform/client-common-core/utils/memoryContextUtils";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { useEffect, useState } from "react";

import { SPICYCHAT_USER_PERMISSIONS } from "@/constants";
import { useInferenceSettings } from "@/hooks";
import useToast from "@/hooks/useToast";
import useAuth from "@/services/useAuth";

// Enum for tracking which fields have been modified by the user
enum ModifiedField {
  INFERENCE_MODEL = "inferenceModel",
  INFERENCE_MAX_NEW_TOKENS = "inferenceMaxNewTokens",
  INFERENCE_TEMPERATURE = "inferenceTemperature",
  INFERENCE_TOP_P = "inferenceTopP",
  INFERENCE_TOP_K = "inferenceTopK",
}

export const useGenerationSettings = () => {
  const {
    inferenceModel: storedInferenceModel,
    inferenceMaxNewTokens: storedInferenceMaxNewTokens,
    inferenceTemperature: storedInferenceTemperature,
    inferenceTopP: storedInferenceTopP,
    inferenceTopK: storedInferenceTopK,
    updateInferenceModel: updateStoredInferenceModel,
    updateInferenceMaxNewTokens: updateStoredInferenceMaxNewTokens,
    updateInferenceTemperature: updateStoredInferenceTemperature,
    updateInferenceTopP: updateStoredInferenceTopP,
    updateInferenceTopK: updateStoredInferenceTopK,
  } = useInferenceSettings();
  const [inferenceModel, setInferenceModel] = useState(storedInferenceModel);
  const [inferenceMaxNewTokens, setInferenceMaxNewTokens] = useState(storedInferenceMaxNewTokens);
  const [inferenceTemperature, setInferenceTemperature] = useState(storedInferenceTemperature);
  const [inferenceTopP, setInferenceTopP] = useState(storedInferenceTopP);
  const [inferenceTopK, setInferenceTopK] = useState(storedInferenceTopK);

  // Track which specific fields have been modified by the user
  const [modifiedFields, setModifiedFields] = useState<Set<string>>(new Set());

  // Sync local state with Redux store ONLY for fields that haven't been modified
  // This allows external changes to unmodified fields while preserving user edits
  useEffect(() => {
    if (!modifiedFields.has(ModifiedField.INFERENCE_MODEL)) {
      setInferenceModel(storedInferenceModel);
    }
  }, [storedInferenceModel, modifiedFields]);

  useEffect(() => {
    if (!modifiedFields.has(ModifiedField.INFERENCE_MAX_NEW_TOKENS)) {
      setInferenceMaxNewTokens(storedInferenceMaxNewTokens);
    }
  }, [storedInferenceMaxNewTokens, modifiedFields]);

  useEffect(() => {
    if (!modifiedFields.has(ModifiedField.INFERENCE_TEMPERATURE)) {
      setInferenceTemperature(storedInferenceTemperature);
    }
  }, [storedInferenceTemperature, modifiedFields]);

  useEffect(() => {
    if (!modifiedFields.has(ModifiedField.INFERENCE_TOP_P)) {
      setInferenceTopP(storedInferenceTopP);
    }
  }, [storedInferenceTopP, modifiedFields]);

  useEffect(() => {
    if (!modifiedFields.has(ModifiedField.INFERENCE_TOP_K)) {
      setInferenceTopK(storedInferenceTopK);
    }
  }, [storedInferenceTopK, modifiedFields]);

  const { permissions: perms } = useAuth();
  const { inferenceModels } = useApp();
  const applicationId = basicAppConfig.applicationId;
  const { displayToast } = useToast();
  const { t } = useTranslation();
  const isSaveDisabled =
    String(inferenceMaxNewTokens) === String(storedInferenceMaxNewTokens) &&
    String(inferenceTemperature) === String(storedInferenceTemperature) &&
    String(inferenceTopP) === String(storedInferenceTopP) &&
    String(inferenceTopK) === String(storedInferenceTopK);

  const permissions = perms as string[];

  const handleArray = (value: number | number[]) => {
    const isArray = Array.isArray(value);
    return !isArray ? value : value[0];
  };

  const updateInferenceModel = (value: string) => {
    setInferenceModel(value);
    setModifiedFields((prev: Set<string>) => new Set(prev).add(ModifiedField.INFERENCE_MODEL));
  };

  const updateInferenceMaxNewTokens = (value: number | number[]) => {
    setInferenceMaxNewTokens(handleArray(value));
    setModifiedFields((prev: Set<string>) => new Set(prev).add(ModifiedField.INFERENCE_MAX_NEW_TOKENS));
  };

  const updateInferenceTemperature = (value: number | number[]) => {
    setInferenceTemperature(handleArray(value));
    setModifiedFields((prev: Set<string>) => new Set(prev).add(ModifiedField.INFERENCE_TEMPERATURE));
  };

  const updateInferenceTopP = (value: number | number[]) => {
    setInferenceTopP(handleArray(value));
    setModifiedFields((prev: Set<string>) => new Set(prev).add(ModifiedField.INFERENCE_TOP_P));
  };

  const updateInferenceTopK = (value: number | number[]) => {
    setInferenceTopK(handleArray(value));
    setModifiedFields((prev: Set<string>) => new Set(prev).add(ModifiedField.INFERENCE_TOP_K));
  };

  const applyInferenceSettings = (settings: { modelId?: string; maxNewTokens?: number; temperature?: number; topP?: number; topK?: number }) => {
    if (settings.modelId !== undefined) {
      updateInferenceModel(settings.modelId);
    }
    if (settings.maxNewTokens !== undefined) {
      updateInferenceMaxNewTokens(settings.maxNewTokens);
    }
    if (settings.temperature !== undefined) {
      updateInferenceTemperature(settings.temperature);
    }
    if (settings.topP !== undefined) {
      updateInferenceTopP(settings.topP);
    }
    if (settings.topK !== undefined) {
      updateInferenceTopK(settings.topK);
    }
  };

  const resetToDefault = () => {
    applyInferenceSettings(DEFAULT_INFERENCE_SETTINGS);
  };

  const handleSave = (onClose: (open: boolean) => void) => {
    updateStoredInferenceModel(inferenceModel);
    updateStoredInferenceMaxNewTokens(inferenceMaxNewTokens);
    updateStoredInferenceTemperature(inferenceTemperature);
    updateStoredInferenceTopP(inferenceTopP);
    updateStoredInferenceTopK(inferenceTopK);
    setModifiedFields(new Set());
    onClose(false);
    displayToast({ message: t("chat:page.header.dropdown.generationSettings.toast.success"), type: NotificationType.success });
  };

  const settingsAllowed = permissions.includes(SPICYCHAT_USER_PERMISSIONS.generation_settings);

  const availableMemory = getAvailableMemory(permissions, inferenceModels, inferenceModel);
  const contextMemory = `${availableMemory / 1024}K`;

  return {
    inferenceModel,
    inferenceMaxNewTokens,
    inferenceTemperature,
    inferenceTopP,
    inferenceTopK,
    updateInferenceModel,
    updateInferenceMaxNewTokens,
    updateInferenceTemperature,
    updateInferenceTopP,
    updateInferenceTopK,
    applyInferenceSettings,
    resetToDefault,
    handleSave,
    settingsAllowed,
    availableMemory,
    contextMemory,
    applicationId,
    isSaveDisabled,
    hasUnsavedChanges: modifiedFields.size > 0,
  };
};
